export const M8_UNIT_3 = {
  id: "m8-u3-ppc-mastery",
  title: "Unit 3: Activities in Progress",
  grammarText: "Present Perfect Continuous affirmative (I've been working), negative (I haven't been sleeping), questions (Have you been studying? / How long has she been cooking?) + for/since duration markers.",
  grammarImage: "/grammar/m8-u3.jpg",
  sets: [

    // SET 1: PPC Affirmative (basic ongoing)
    {
      id: "m8-u3-s1",
      title: "Set 1: Ongoing Activities",
      context: `*Neighbor chat over fence*\n\n"You look exhausted!" "I've been renovating the house all week!"\n\n*Saturday afternoon*\n\nPaint everywhere. Tools scattered.`,
      questions: [
        {type: "passage", passage: `"You look exhausted, Sarah!"\n"I've been renovating the house all week. Been painting since Monday!"\n"Wow! I've been gardening for hours too. These projects never end!"`},
        {type: "mc", q: "What has Sarah been doing?", options: ["Renovating house", "Gardening", "Cooking"], a: "Renovating house"},
        {type: "order", q: "Traduce: 'He estado renovando'", words: ["I've", "been", "renovating."], a: "I've been renovating.", extra: ["I renovate", "I'm renovating"], tip: "Have/has + been + ing = ongoing recent."},
        {type: "mc", q: "Sarah's been painting since ___", options: ["Monday", "Saturday", "hours"], a: "Monday"},
        {type: "input", q: "I've ___ painting all week.", a: "been"},
        {type: "passage", passage: `"The kitchen looks amazing now!"\n"Thanks! I've been tiling it for three days. My dad's been helping too. He's been fixing the bathtub."`},
        {type: "mc", q: "Tiling for how long?", options: ["3 days", "3 weeks", "3 hours"], a: "3 days"},
        {type: "order", q: "Traduce: 'Mi papá ha estado ayudando'", words: ["My", "dad's", "been", "helping."], a: "My dad's been helping.", extra: ["My dad helps"], tip: "Dad's = dad has."},
        {type: "input", q: "He's been ___ the bathtub.", a: "fixing"},
        {type: "order", q: "Full answer: I've been tiling ___ three days.", words: ["for"], a: "I've been tiling for three days.", successTip: "PPC FORMED! Have/has + been + ing shows ongoing recent activity. 'Been working all day!' 🔨"}
      ]
    },

    // SET 2: PPC with Evidence
    {
      id: "m8-u3-s2",
      title: "Set 2: Visible Evidence",
      context: `*Kitchen scene*\n\nFlour everywhere. Delicious smell. Tired baker.\n\n*Afternoon baking*\n\n"You're covered in flour!"`,
      questions: [
        {type: "passage", passage: `"You're covered in flour, Emma!"\n"I know! I've been baking all afternoon. Been making cakes for my daughter's party. She's been helping too, but mostly eating the batter!"`},
        {type: "mc", q: "Evidence: Emma is covered in ___", options: ["flour", "paint", "dirt"], a: "flour"},
        {type: "order", q: "Traduce: 'He estado horneando'", words: ["I've", "been", "baking."], a: "I've been baking.", extra: ["I bake", "I baked"], tip: "Evidence visible → PPC explains it."},
        {type: "mc", q: "Who's been helping?", options: ["Her daughter", "Her son", "Her husband"], a: "Her daughter"},
        {type: "input", q: "She's been ___ all afternoon.", a: "baking"},
        {type: "passage", passage: `"Your hands are dirty!"\n"I've been gardening since this morning. Been planting flowers and cutting the grass. It's hard work but relaxing."`},
        {type: "mc", q: "Evidence: dirty ___", options: ["hands", "face", "shoes"], a: "hands"},
        {type: "order", q: "Traduce: 'He estado plantando flores'", words: ["I've", "been", "planting", "flowers."], a: "I've been planting flowers.", extra: ["I plant"], tip: "Been + ing = explains current state."},
        {type: "input", q: "I've been ___ since morning.", a: "gardening"},
        {type: "order", q: "Evidence talk: You're sweating! ___ been jogging?", words: ["Have", "you"], a: "You're sweating! Have you been jogging?", successTip: "EVIDENCE MASTER! See result → explain with PPC. 'Covered in paint? Been decorating!' Natural B2. 👀"}
      ]
    },

    // SET 3: PPC Negative (haven't been)
    {
      id: "m8-u3-s3",
      title: "Set 3: What You Haven't Done",
      context: `*Lazy weekend confession*\n\n"What have you been doing?" "Actually... nothing productive!"\n\n*Sunday evening*\n\nGuilt setting in.`,
      questions: [
        {type: "passage", passage: `"So, what have you been doing this weekend?"\n"Honestly? I haven't been doing much. I haven't been working on my project. Just been relaxing and watching TV!"`},
        {type: "mc", q: "What hasn't he been doing?", options: ["Working on project", "Relaxing", "Watching TV"], a: "Working on project"},
        {type: "order", q: "Traduce: 'No he estado trabajando'", words: ["I", "haven't", "been", "working."], a: "I haven't been working.", extra: ["I don't work", "I'm not working"], tip: "Haven't + been + ing = negative PPC."},
        {type: "mc", q: "What has he been doing?", options: ["Relaxing", "Studying", "Exercising"], a: "Relaxing"},
        {type: "input", q: "I ___ been doing much.", a: "haven't"},
        {type: "passage", passage: `"You look well rested!"\n"I am! I haven't been sleeping well lately, but last night I slept 10 hours. My neighbor hasn't been making noise anymore."`},
        {type: "mc", q: "How did he sleep last night?", options: ["10 hours", "5 hours", "badly"], a: "10 hours"},
        {type: "order", q: "Traduce: 'No he estado durmiendo bien'", words: ["I", "haven't", "been", "sleeping", "well."], a: "I haven't been sleeping well.", extra: ["I don't sleep"], tip: "Lately = recientemente."},
        {type: "input", q: "Neighbor ___ been making noise.", a: "hasn't"},
        {type: "order", q: "Negative full: I haven't been studying. I've been ___ lazy!", words: ["being"], a: "I haven't been studying. I've been being lazy!", successTip: "NEGATIVE PPC! 'Haven't been working' vs 'Have been relaxing'. Contrast actions! 😴"}
      ]
    },

    // SET 4: Negative + Contrast
    {
      id: "m8-u3-s4",
      title: "Set 4: What Changed",
      context: `*Lifestyle update chat*\n\n"You look different!" "I've been making changes!"\n\n*Coffee shop reunion*\n\nOld friends catching up.`,
      questions: [
        {type: "passage", passage: `"You look great! What's your secret?"\n"Thanks! I haven't been eating junk food anymore. I've been cooking healthy meals and exercising. Haven't been staying up late either!"`},
        {type: "mc", q: "What hasn't she been eating?", options: ["Junk food", "Healthy meals", "Vegetables"], a: "Junk food"},
        {type: "order", q: "Traduce: 'No he estado comiendo comida chatarra'", words: ["I", "haven't", "been", "eating", "junk", "food."], a: "I haven't been eating junk food.", extra: ["I don't eat"], tip: "Change in habit = PPC."},
        {type: "mc", q: "What has she been doing?", options: ["Cooking healthy meals", "Eating junk", "Staying up late"], a: "Cooking healthy meals"},
        {type: "input", q: "She's been ___.", a: "exercising"},
        {type: "passage", passage: `"Your English is so much better!"\n"I know! I haven't been watching TV in Spanish anymore. I've been watching everything in English. Been practicing every day!"`},
        {type: "mc", q: "What language TV now?", options: ["English", "Spanish", "French"], a: "English"},
        {type: "order", q: "Traduce: 'He estado practicando cada día'", words: ["I've", "been", "practicing", "every", "day."], a: "I've been practicing every day.", extra: ["I practice"], tip: "Every day = daily routine."},
        {type: "input", q: "I ___ been watching Spanish TV.", a: "haven't"},
        {type: "order", q: "Contrast: I haven't been wasting time. I've been ___ hard!", words: ["working"], a: "I haven't been wasting time. I've been working hard!", successTip: "CONTRAST PRO! 'Haven't been doing X but HAVE been doing Y!' Show change perfectly. 💪"}
      ]
    },

    // SET 5: Questions (Have you been...?)
    {
      id: "m8-u3-s5",
      title: "Set 5: Asking About Activities",
      context: `*Band rehearsal break*\n\n"Have you been practicing?" Evidence-based questions.\n\n*Music studio*\n\nInstruments everywhere. Coffee cups.`,
      questions: [
        {type: "passage", passage: `"You sound amazing on guitar! Have you been practicing?"\n"Yes! I've been playing for hours every day. Have you been working on the drums?"\n"A bit. I haven't been practicing as much as you!"`},
        {type: "mc", q: "Guitar player response?", options: ["Yes, practicing hours", "No, not much", "Just started"], a: "Yes, practicing hours"},
        {type: "order", q: "Traduce: '¿Has estado practicando?'", words: ["Have", "you", "been", "practicing?"], a: "Have you been practicing?", extra: ["Do you practice", "Are you practicing"], tip: "Have/has + subject + been + ing."},
        {type: "mc", q: "Drummer's practice?", options: ["Not as much", "Every day", "None"], a: "Not as much"},
        {type: "input", q: "___ you been working on drums?", a: "Have"},
        {type: "passage", passage: `"Your voice is getting stronger!"\n"Thanks! Have you been listening to my recordings?"\n"Yes! Has your teacher been giving you new exercises?"\n"She has! They're really helping."`},
        {type: "mc", q: "Teacher been giving what?", options: ["New exercises", "Recordings", "Nothing"], a: "New exercises"},
        {type: "order", q: "Traduce: '¿Has estado escuchando?'", words: ["Have", "you", "been", "listening?"], a: "Have you been listening?", extra: ["Do you listen"], tip: "Evidence → question."},
        {type: "input", q: "___ your teacher been helping?", a: "Has"},
        {type: "order", q: "Question chain: Have you been practicing? -Yes! ___ you?", words: ["Have"], a: "Have you been practicing? -Yes! Have you?", successTip: "QUESTION FORMED! Have/has + subject + been + ing. Natural curiosity questions. 🎸"}
      ]
    },

    // SET 6: Yes/No PPC Questions
    {
      id: "m8-u3-s6",
      title: "Set 6: Quick Questions",
      context: `*Workplace small talk*\n\n"Have you been working late?" Quick check-ins.\n\n*Office Monday morning*\n\nCoffee machine gathering.`,
      questions: [
        {type: "passage", passage: `"You look tired. Have you been working late?"\n"Yes, I have. I've been finishing a big project. Have you been busy too?"\n"Not really. I've been leaving on time this week!"`},
        {type: "mc", q: "Has he been working late?", options: ["Yes", "No", "Sometimes"], a: "Yes"},
        {type: "order", q: "Traduce: '¿Has estado trabajando tarde?'", words: ["Have", "you", "been", "working", "late?"], a: "Have you been working late?", extra: ["Do you work late"], tip: "Recent routine → PPC question."},
        {type: "mc", q: "She's been leaving ___", options: ["on time", "late", "early"], a: "on time"},
        {type: "input", q: "Yes, I ___.", a: "have"},
        {type: "passage", passage: `"Has anyone been using my desk?"\n"No, nobody has. Why? Is something missing?"\n"My stapler! Has someone been borrowing my stuff?"\n"Maybe the cleaning crew. They've been here all week."`},
        {type: "mc", q: "What's missing?", options: ["Stapler", "Desk", "Computer"], a: "Stapler"},
        {type: "order", q: "Traduce: '¿Alguien ha estado usando?'", words: ["Has", "anyone", "been", "using?"], a: "Has anyone been using?", extra: ["Is someone using"], tip: "Anyone = alguien (questions)."},
        {type: "input", q: "___ someone been borrowing my stuff?", a: "Has"},
        {type: "order", q: "Quick Q&A: Have you been studying? -Yes, I ___!", words: ["have"], a: "Have you been studying? -Yes, I have!", successTip: "YES/NO PPC! Short answers: 'Yes, I have / No, I haven't.' Natural replies. ✅"}
      ]
    },

    // SET 7: How Long Questions
    {
      id: "m8-u3-s7",
      title: "Set 7: Duration Questions",
      context: `*Meeting old friend*\n\n"How long have you been living here?"\n\n*Café reunion*\n\nYears since last meeting.`,
      questions: [
        {type: "passage", passage: `"How long have you been living in Mexico City?"\n"For three years now. I moved here in 2023. How long have you been working at the university?"\n"Since last September. About five months."`},
        {type: "mc", q: "Living there how long?", options: ["3 years", "5 months", "Since 2023"], a: "3 years"},
        {type: "order", q: "Traduce: '¿Cuánto tiempo has estado viviendo aquí?'", words: ["How", "long", "have", "you", "been", "living", "here?"], a: "How long have you been living here?", extra: ["How much time"], tip: "How long + have/has + been + ing."},
        {type: "mc", q: "Working since ___?", options: ["Last September", "3 years", "2023"], a: "Last September"},
        {type: "input", q: "How ___ have you been working?", a: "long"},
        {type: "passage", passage: `"Your Spanish is amazing! How long have you been studying it?"\n"Thanks! I've been learning for five years. Been taking classes since high school. How long have you been teaching?"\n"For ten years now!"`},
        {type: "mc", q: "Learning Spanish how long?", options: ["5 years", "10 years", "Since now"], a: "5 years"},
        {type: "order", q: "Traduce: '¿Cuánto llevas enseñando?'", words: ["How", "long", "have", "you", "been", "teaching?"], a: "How long have you been teaching?", extra: ["How many years"], tip: "How long = cuánto tiempo."},
        {type: "input", q: "I've been learning ___ five years.", a: "for"},
        {type: "order", q: "Duration Q: How long have you been studying? -___ high school!", words: ["Since"], a: "How long have you been studying? -Since high school!", successTip: "HOW LONG MASTER! Question + for/since answer. Perfect conversation flow. ⏰"}
      ]
    },

    // SET 8: How Long with Evidence
    {
      id: "m8-u3-s8",
      title: "Set 8: Evidence + Duration",
      context: `*Noticing changes*\n\n"You're so fit! How long training?"\n\n*Gym encounter*\n\nVisible transformation.`,
      questions: [
        {type: "passage", passage: `"Wow! You look so fit! How long have you been working out?"\n"Thanks! I've been training for six months. Been going to the gym every day since January. How long have you been coming here?"\n"Just two weeks. You're inspiring me!"`},
        {type: "mc", q: "Training how long?", options: ["6 months", "2 weeks", "Every day"], a: "6 months"},
        {type: "order", q: "Traduce: '¿Cuánto tiempo has estado entrenando?'", words: ["How", "long", "have", "you", "been", "training?"], a: "How long have you been training?", extra: ["How much time"], tip: "Evidence visible → ask duration."},
        {type: "mc", q: "Going since ___?", options: ["January", "6 months", "2 weeks"], a: "January"},
        {type: "input", q: "I've been training ___ six months.", a: "for"},
        {type: "passage", passage: `"Your garden looks beautiful! How long have you been working on it?"\n"I've been gardening for years, but I've been planning this design since last spring. Been planting these flowers for three months."`},
        {type: "mc", q: "Gardening experience?", options: ["Years", "Months", "Weeks"], a: "Years"},
        {type: "order", q: "Traduce: 'He estado planeando este diseño'", words: ["I've", "been", "planning", "this", "design."], a: "I've been planning this design.", extra: ["I plan"], tip: "Been + ing = ongoing project."},
        {type: "input", q: "Planting flowers ___ three months.", a: "for"},
        {type: "order", q: "Evidence combo: You're exhausted! How long ___ been working?", words: ["have", "you"], a: "You're exhausted! How long have you been working?", successTip: "EVIDENCE + DURATION! See result → ask 'How long been...?' Perfect B2 curiosity. 💬"}
      ]
    },

    // SET 9: Mixed PPC Forms
    {
      id: "m8-u3-s9",
      title: "Set 9: Real Conversations",
      context: `*Family video call*\n\nUpdating relatives. Mixed questions and answers.\n\n*Sunday evening*\n\nCatching up on everything.`,
      questions: [
        {type: "passage", passage: `"Hi mom! How have you been?"\n"Great! I've been taking painting classes. Been going every Tuesday for two months now!"\n"That's wonderful! Have you been painting at home too?"\n"Yes! I've been working on a portrait. Haven't finished it yet though."`},
        {type: "mc", q: "Classes how often?", options: ["Every Tuesday", "Every day", "Every month"], a: "Every Tuesday"},
        {type: "order", q: "Traduce: 'He estado tomando clases'", words: ["I've", "been", "taking", "classes."], a: "I've been taking classes.", extra: ["I take"], tip: "Regular recent activity."},
        {type: "mc", q: "What hasn't she finished?", options: ["Portrait", "Classes", "Nothing"], a: "Portrait"},
        {type: "input", q: "I've been going ___ two months.", a: "for"},
        {type: "passage", passage: `"How's your brother?"\n"He's good! He's been learning guitar. Been practicing every day since his birthday."\n"Has he been taking lessons?"\n"No, he's been teaching himself. He's really talented!"`},
        {type: "mc", q: "Guitar since ___?", options: ["His birthday", "Every day", "Two months"], a: "His birthday"},
        {type: "order", q: "Traduce: '¿Ha estado tomando lecciones?'", words: ["Has", "he", "been", "taking", "lessons?"], a: "Has he been taking lessons?", extra: ["Does he take"], tip: "Recent habit → PPC question."},
        {type: "input", q: "He's been teaching ___.", a: "himself"},
        {type: "order", q: "Family update: I've been studying hard. ___ you been doing?", words: ["What", "have"], a: "I've been studying hard. What have you been doing?", successTip: "CONVERSATION MIX! Affirmative + negative + questions. Natural family chat flow. 👨‍👩‍👧"}
      ]
    },

    // SET 10: B2 Integration Mastery
    {
      id: "m8-u3-s10",
      title: "Set 10: PPC Mastery",
      context: `*EducaT Reunion Event*\n\nStudents sharing all their progress. Full PPC stories.\n\n*Annual gathering*\n\nEveryone's achievements and journeys.`,
      questions: [
        {type: "passage", passage: `"Welcome everyone! Please share what you've been doing this year!"\n"I've been learning three languages! Been studying Spanish for six months and Portuguese for three. Haven't been sleeping much though!"\n"Amazing! Have you been using apps or taking classes?"\n"Both! I've been taking online classes since January."`},
        {type: "mc", q: "How many languages learning?", options: ["3", "2", "6"], a: "3"},
        {type: "order", q: "Traduce: 'He estado aprendiendo tres idiomas'", words: ["I've", "been", "learning", "three", "languages."], a: "I've been learning three languages.", extra: ["I learn"], tip: "Ongoing progress."},
        {type: "mc", q: "What hasn't he been doing?", options: ["Sleeping much", "Studying", "Taking classes"], a: "Sleeping much"},
        {type: "input", q: "How long ___ you been studying Spanish?", a: "have"},
        {type: "passage", passage: `"I haven't been working much this year. I've been volunteering instead! Been helping at an animal shelter for eight months. It's been the best experience!"\n"How long have you been doing that every week?"\n"I've been going three times a week since March!"`},
        {type: "mc", q: "What's he been doing instead of working?", options: ["Volunteering", "Studying", "Sleeping"], a: "Volunteering"},
        {type: "order", q: "Traduce: 'He estado haciendo voluntariado'", words: ["I've", "been", "volunteering."], a: "I've been volunteering.", extra: ["I volunteer"], tip: "New activity focus."},
        {type: "input", q: "Going ___ times a week.", a: "three"},
        {type: "passage", passage: `"What about you? Have you been traveling?"\n"Not really. I've been saving money! Haven't been going out much. Been cooking at home and learning new recipes for a year now!"\n"That sounds wonderful! How long have you been saving for?"\n"Since last January. I've been planning a big trip!"`},
        {type: "order", q: "Traduce: 'No he estado saliendo mucho'", words: ["I", "haven't", "been", "going", "out", "much."], a: "I haven't been going out much.", extra: ["I don't go out"], tip: "Change in habits."},
        {type: "order", q: "Ultimate PPC: What have you been doing? -I've been learning, haven't been wasting time, and ___ been planning my future!", words: ["I've"], a: "What have you been doing? -I've been learning, haven't been wasting time, and I've been planning my future!", successTip: "M8-U3 CONQUERED! PPC affirmative/negative/questions + for/since + evidence = B2 storytelling mastery! 'Been working on it for months!' Native fluency. 🏆🎯"}
      ]
    }
  ],

  readingSet: {
    id: "m8-u3-reading",
    title: "Reading: Progress Stories",
    category: "reading",
    questions: [
      {
        type: "passage",
        passage: `**Renovation Diary Entry**\n\nWeek 3: Exhausted but happy! I've been renovating for three weeks now. Been painting the bedroom since Monday and tiling the bathroom. My brother's been helping on weekends. Haven't been sleeping well because I'm so excited to see the final result!`
      },
      {
        type: "mc",
        q: "Renovating how long total?",
        options: ["3 weeks", "3 days", "Monday"],
        a: "3 weeks"
      },
      {
        type: "order",
        q: "Traduce: 'He estado pintando desde el lunes'",
        words: ["I've", "been", "painting", "since", "Monday."],
        a: "I've been painting since Monday.",
        tip: "Since + day = start point."
      },
      {
        type: "mc",
        q: "Who's been helping?",
        options: ["Brother", "Sister", "Dad"],
        a: "Brother"
      },
      {
        type: "passage",
        passage: `**Music Practice Chat**\n\n"How long have you been playing drums?"\n"For five years! I've been practicing every day since I joined the band. Haven't missed a single rehearsal. Have you been taking guitar lessons?"\n"Yes! I've been learning for two years now. My teacher's been teaching me jazz."`
      },
      {
        type: "mc",
        q: "Drums for how long?",
        options: ["5 years", "2 years", "Every day"],
        a: "5 years"
      },
      {
        type: "input",
        q: "Guitar learning ___ two years.",
        a: "for"
      },
      {
        type: "passage",
        passage: `**DIY Weekend Update**\n\nSunday evening: What a weekend! I've been working on the house for two days straight. Been putting up shelves, fixing the bathtub, and making curtains. Haven't been this tired in months! But the living room looks fantastic now.`
      },
      {
        type: "order",
        q: "Traduce: 'He estado trabajando por dos días'",
        words: ["I've", "been", "working", "for", "two", "days."],
        a: "I've been working for two days.",
        successTip: "READING COMPLETE! PPC in real contexts mastered."
      },
      {
        type: "mc",
        q: "What looks fantastic?",
        options: ["Living room", "Bathroom", "Bedroom"],
        a: "Living room"
      }
    ]
  },

  reviewSet: {
    id: "m8-u3-review",
    title: "Unit 3 Mastery Review",
    category: "review",
    questions: [
      {
        type: "passage",
        passage: `"You look exhausted!"\n"I've been studying for exams all week. Been reviewing notes every night. Haven't been sleeping much!"`
      },
      {
        type: "mc",
        q: "Why exhausted?",
        options: ["Studying for exams", "Working late", "Exercising"],
        a: "Studying for exams"
      },
      {
        type: "order",
        q: "Traduce: 'He estado estudiando toda la semana'",
        words: ["I've", "been", "studying", "all", "week."],
        a: "I've been studying all week.",
        tip: "All week = toda la semana."
      },
      {
        type: "mc",
        q: "What hasn't he been doing?",
        options: ["Sleeping much", "Studying", "Reviewing"],
        a: "Sleeping much"
      },
      {
        type: "passage",
        passage: `"How long have you been working here?"\n"For three years. I've been managing the team since last January. Have you been enjoying your new job?"\n"Yes! I've been learning so much!"`
      },
      {
        type: "input",
        q: "Working ___ three years.",
        a: "for"
      },
      {
        type: "mc",
        q: "Managing since ___?",
        options: ["Last January", "3 years", "New job"],
        a: "Last January"
      },
      {
        type: "order",
        q: "Question form: How long ___ you been learning?",
        words: ["have"],
        a: "How long have you been learning?"
      },
      {
        type: "passage",
        passage: `"Your garden is beautiful!"\n"Thanks! I've been planting flowers for months. Been watering them every morning since spring. Haven't been using any chemicals either!"`
      },
      {
        type: "mc",
        q: "Watering since ___?",
        options: ["Spring", "Months", "Morning"],
        a: "Spring"
      },
      {
        type: "input",
        q: "I ___ been using chemicals.",
        a: "haven't"
      },
      {
        type: "order",
        q: "Ultimate mastery: I've been working ___ hours. How long have ___ been here?",
        words: ["for", "you"],
        a: "I've been working for hours. How long have you been here?",
        successTip: "M8-U3 MASTERED! PPC affirmative/negative/questions + for/since + evidence = Complete B2 fluency. 'Been practicing for years!' 🌟🎯"
      }
    ]
  }
};
